import { authFetch, getSessionToken } from "site/services/auth-session.service";
import { subscriptionApiBaseUrl } from "site/services/xq-api-base-url";

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = getSessionToken();
  const h: Record<string, string> = { ...extra };
  if (token) {
    h.Authorization = `Bearer ${token}`;
  }
  return h;
}

function apiBase(): string {
  const base = subscriptionApiBaseUrl();
  if (!base) {
    throw new Error("VITE_XQ_API_BASE_URL is not set");
  }
  return base;
}

export async function editorOpenAiChatCompletions(body: unknown): Promise<Response> {
  return authFetch(`${apiBase()}/editor/chat/completions`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
}

export async function editorOpenAiChatCompletionsStream(
  body: Record<string, unknown>,
  onChunk?: (text: string) => void,
): Promise<string> {
  const r = await editorOpenAiChatCompletions({ ...body, stream: true });
  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    throw new Error(`OpenAI chat: HTTP ${r.status}${detail ? ` — ${detail}` : ""}`);
  }
  return readOpenAiChatStream(r, onChunk);
}

export async function readOpenAiChatStream(
  response: Response,
  onChunk?: (text: string) => void,
): Promise<string> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("OpenAI chat: empty response body");
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let content = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data:")) {
        continue;
      }
      const data = trimmed.slice(5).trim();
      if (!data || data === "[DONE]") {
        continue;
      }
      let json: {
        choices?: Array<{ delta?: { content?: string } }>;
      };
      try {
        json = JSON.parse(data) as {
          choices?: Array<{ delta?: { content?: string } }>;
        };
      } catch {
        continue;
      }
      const delta = json.choices?.[0]?.delta?.content;
      if (typeof delta !== "string" || !delta) {
        continue;
      }
      content += delta;
      onChunk?.(delta);
    }
  }

  return content;
}

export async function editorOpenAiSpeech(body: unknown): Promise<Response> {
  return authFetch(`${apiBase()}/editor/audio/speech`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
}

export async function editorOpenAiDialogSpeech(body: unknown): Promise<Response> {
  return authFetch(`${apiBase()}/editor/audio/dialog`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
}
